import {
  Button,
  ButtonColor,
  ButtonSize,
  Checkbox,
  Combobox,
  Option,
} from '@code/innovance-react-ui-kit';
import React, { ChangeEvent, FC, useCallback, useState } from 'react';
import { Controller, FieldError, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { DocumentPageValidation } from 'src/front/api/apiInstance';
import { ReferenceDataType } from 'src/front/api/generated';
import usePRM from 'src/front/common/hooks/usePRM';

import useYupResolver from 'src/front/common/hooks/useYupResolver';
import { isIncorrectInnNumber } from 'src/front/common/utils/errorUtils';
import { formatMaskedText } from 'src/front/common/utils/formUtils';
import FormModal from 'src/front/components/FormModal';

import {
  ButtonRow,
  FormElement,
  FormRow,
  ResetButton,
  Subtitle,
  Title,
} from 'src/front/components/FormStyles';
import { IdentityFormData } from 'src/front/components/identity/IdentityFormData';
import IDENTITY_COMPONENT_MAP, {
  IDENTITY_SCHEMA_MAP,
} from 'src/front/components/identity/IdentityMap';
import { MODAL_VIEW } from 'src/front/constant';
import styled from 'styled-components';
import * as Yup from 'yup';
import { FormProps } from './FormProps';

const defaultValues = {
  identityType: null,
  identityDocumentSerialCode: '',
  identityDocumentSerialNumber: '',
  issueDate: null,
  expireDate: null,
  issuedBy: '',
  issueCountry: null,
  unitCode: '',
  isActive: true,
};

const CheckBoxWrapper = styled.div`
  display: flex;
  height: 100%;

  align-items: center;
`;

interface Props {
  identityTypes: Option[];
  onClose: () => void;
  submit: (formData: IdentityFormData) => void;
  formValues?: IdentityFormData;
  isNew?: boolean;
  showActiveFlag?: boolean;
}

const IdentityModal: FC<Props> = ({
  formValues,
  identityTypes,
  onClose,
  submit,
  isNew,
  showActiveFlag,
}) => {
  const { t } = useTranslation();

  const countries = usePRM(ReferenceDataType.COUNTRIES);
  const [identitySchema, setIdentitySchema] = useState<Yup.ObjectSchema>(
    IDENTITY_SCHEMA_MAP[(formValues?.identityType?.value as string) || 'default']
  );

  const resolver = useYupResolver(identitySchema, {
    todayMaxControl: true,
    todayMinControl: true,
  });

  const formMethods = useForm<IdentityFormData>({
    defaultValues: formValues || defaultValues,
    resolver,
  });
  const {
    watch,
    errors,
    clearErrors,
    setValue,
    control,
    handleSubmit,
    formState,
    setError,
    reset,
  } = formMethods;

  const handleReset = useCallback(
    (identityType?: Option | null): void => {
      const identityFormData = formValues ?? defaultValues;
      reset(
        { ...identityFormData, ...{ identityType } },
        {
          touched: false,
          isDirty: false,
          errors: false,
        }
      );
      clearErrors();
    },
    [clearErrors, formValues, reset]
  );
  const handleIdentityTypeChange = useCallback(
    (callback) => (type: Option | null): void => {
      handleReset(type);
      callback(type);
      setIdentitySchema(IDENTITY_SCHEMA_MAP[(type?.value as string) || 'default']);
    },
    [handleReset, setIdentitySchema]
  );
  const handleDateChange = (fieldName: 'issueDate' | 'expireDate') => (date: Date): void =>
    setValue(fieldName, date, {
      shouldValidate: formState.isSubmitted && !!date,
      shouldDirty: formState.isSubmitted || !!date,
    });

  const handleTextChange = (fieldName: string, mask?: string) => (text: string) =>
    setValue(fieldName, formatMaskedText(text, mask), {
      shouldValidate: formState.isSubmitted && !!text,
      shouldDirty: formState.isSubmitted || !!text,
    });

  const handleIsActiveChange = async ({ target }: ChangeEvent<HTMLInputElement>): Promise<void> => {
    setValue('isActive', target.checked, { shouldDirty: true });
  };
  const handleCountryChange = (option: Option | null): void => {
    setValue('issueCountry', option, { shouldValidate: true, shouldDirty: true });
  };

  const watchIdentityType = watch('identityType');

  const onReset = (): void => {
    handleReset();
  };
  const onSubmit = async (formData: IdentityFormData): Promise<void> => {
    const { identityType, identityDocumentSerialNumber } = formData;
    try {
      if (identityType && identityDocumentSerialNumber) {
        const { data } = await DocumentPageValidation.validateAddIdentityData({
          documentType: identityType.value,
          identityDocumentSerialNumber,
        });
        if (data.result === 'OK') {
          await submit({ ...formValues, ...formData });
        }
      }
    } catch (error) {
      if (isIncorrectInnNumber(error)) {
        setError('identityDocumentSerialNumber', {
          type: 'pattern',
          message: error?.errorMessage,
        });
      }
    }
  };
  const Component: FC<FormProps> | null = watchIdentityType
    ? IDENTITY_COMPONENT_MAP[watchIdentityType.value as string]
    : null;
  return (
    <FormModal onClose={onClose} size={MODAL_VIEW.MIDDLE} zIndex={900}>
      <Title>
        <Trans i18nKey="cardEntry_documentEntry_title" />
      </Title>
      <Subtitle>
        <Trans i18nKey="cardEntry_documentEntry_table1_subtitle" />
      </Subtitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="identityType"
              render={({ onChange, value }) => (
                <Combobox
                  disabled={!!formValues?.identityType}
                  errorMessage={t((errors?.identityType as FieldError)?.message as string)}
                  items={identityTypes}
                  label={t('general_documentType_label')}
                  name="identityType"
                  onChange={handleIdentityTypeChange(onChange)}
                  searchPlaceholder={t('cardEntry_documentEntry_searchDocumentType_text')}
                  value={value}
                  searchable
                />
              )}
            />
          </FormElement>
          {showActiveFlag && (
            <FormElement>
              <CheckBoxWrapper>
                <Controller
                  control={control}
                  name="isActive"
                  render={({ onChange, value }) => (
                    <Checkbox
                      disabled={isNew}
                      id="isActive"
                      label={t('changeFundClientInfo_activityFlag_text')}
                      onChange={(val) => {
                        onChange(val);
                        handleIsActiveChange(val).then(() => null);
                      }}
                      value={value}
                    />
                  )}
                />
              </CheckBoxWrapper>
            </FormElement>
          )}
        </FormRow>
        {!!Component && (
          <Component
            countries={countries}
            formMethods={formMethods}
            handleCountryChange={handleCountryChange}
            handleDateChange={handleDateChange}
            handleTextChange={handleTextChange}
          />
        )}
        <ButtonRow>
          {formState.isDirty && (
            <ResetButton
              color={ButtonColor.Secondary}
              onClick={onReset}
              size={ButtonSize.Medium}
              text={t('general_components_reset_button')}
              type="reset"
            />
          )}
          {watchIdentityType && (
            <Button
              color={ButtonColor.Primary}
              disabled={formState.isSubmitting}
              size={ButtonSize.Medium}
              text={t('general_save_button')}
              type="submit"
            />
          )}
        </ButtonRow>
      </form>
    </FormModal>
  );
};

export default IdentityModal;
